import { QueryResult } from '@apollo/client';
import { UUID } from '@cmg/common';
import React from 'react';

import { useGetOfferingEntitlementsQuery } from './useGetOfferingEntitlementsQuery';

export type Props = {
  /** offeringId to fetch entitlements */
  offeringId?: UUID;
  /** requiredEntitlements that we want to check for on the offering */
  requiredEntitlements: string[];
  /** requireAllEntitlements whether we want all requiredEntitlements to exist on the offering when true or some when false */
  requireAllEntitlements?: boolean;
  /** whether to include private offerings in the check */
  withPrivateOfferings?: boolean;
};

/**
 * useCheckEntitlements checks if requiredEntitlements are defined for a given offeringId
 * returns the loading, called and error state of the request along with hasEntitlements: boolean
 *
 * While the request is made to fetch entitlements hasEntitlements is undefined
 */
export function useCheckEntitlements({
  offeringId,
  requiredEntitlements = [],
  requireAllEntitlements = true,
  withPrivateOfferings = false,
}: Props): Pick<QueryResult, 'error' | 'loading' | 'called'> & {
  hasEntitlements?: boolean;
} {
  const {
    data: configuredEntitlements,
    error,
    loading,
    called,
  } = useGetOfferingEntitlementsQuery({ offeringId, withPrivateOfferings });

  const hasEntitlements = React.useMemo(() => {
    if (!configuredEntitlements) {
      return undefined;
    }

    return requireAllEntitlements
      ? requiredEntitlements.every(entitlement => configuredEntitlements?.includes(entitlement))
      : requiredEntitlements.some(entitlement => configuredEntitlements?.includes(entitlement));
  }, [configuredEntitlements, requireAllEntitlements, requiredEntitlements]);

  return {
    hasEntitlements,
    loading,
    error,
    called,
  };
}
